import CheckBox from "components/check-box";
import ModalTemplate from "components/modal/modal-template";
import Typography from "components/typography";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Colors } from "utils/colors";
import { Countries } from "constants/countries";
import { ICountry } from "../../..";

interface IProps {
  close: () => void;
  confirm: (countries: ICountry[]) => void;
  countries: ICountry[];
}
const EditCountries = ({ close, confirm, countries }: IProps) => {
  const [activeContinent, setActiveContinent] = useState("Asia");
  const [selectAll, setSelectAll] = useState(true);
  const [selectedCountries, setSelectedCountries] = useState([...countries]);

  const continents = [
    "Asia",
    "Europe",
    "Africa",
    "Australia",
    "North America",
    "South America",
    "Antarctica"
  ];

  const removeCountry = (countryName: string) => {
    const newCountries = selectedCountries.filter(
      (item) => item.country !== countryName
    );

    setSelectedCountries(newCountries);
  };

  const addCountry = (countryName: string) => {
    setSelectedCountries([
      ...selectedCountries,
      { country: countryName, continent: activeContinent }
    ]);
  };

  const isCountryInArray = (countryName: string) => {
    return selectedCountries.some((item) => item.country === countryName);
  };

  const removeCountriesByContinent = (continentName: string) => {
    const newCountries = selectedCountries.filter(
      (item) => item.continent !== continentName
    );

    setSelectedCountries(newCountries);
  };

  const addCountriesByContinent = (continentName: string) => {
    const newCountries = Countries.filter(
      (item) => item.continent === continentName
    );
    const uniqueCountries = newCountries.filter(
      (newCountry) =>
        !selectedCountries.some(
          (selectedCountry) => selectedCountry.country === newCountry.country
        )
    );

    setSelectedCountries([...selectedCountries, ...uniqueCountries]);
  };

  const countCountriesByContinent = (
    array: { continent: string }[],
    continentName: string
  ): number => {
    return array.filter((item) => item.continent === continentName).length;
  };

  useEffect(() => {
    if (
      countCountriesByContinent(selectedCountries, activeContinent) ===
      countCountriesByContinent(Countries, activeContinent)
    ) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedCountries, activeContinent]);

  return (
    <ModalTemplate
      close={close}
      confirm={() => {
        confirm(selectedCountries);
        close();
      }}
      header="Edit Countries"
      confirmText="Save"
    >
      <Typography align="center" mb="2.4" color={Colors.white300}>
        Uncheck the countries that you don’t want us to deliver to
      </Typography>
      <Body>
        <ContinentsContainer>
          {continents.map((c, i) => {
            const active = activeContinent === c;
            return (
              <ContinentItem key={i} onClick={() => setActiveContinent(c)}>
                <PlanetItem>
                  {active ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="100%"
                      height="100%"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        opacity="0.5"
                        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                        fill="#FEB73D"
                      />
                      <path
                        d="M8.57613 9.44737C8.38887 7.36316 6.78157 5.42105 6.00132 4.71053L5.57031 4.34189C7.30889 2.88037 9.5523 2 12.0014 2C14.2147 2 16.2602 2.7191 17.9168 3.93642C18.1508 4.64695 17.705 6.13158 17.2369 6.84211C17.0673 7.09947 16.6828 7.41898 16.2612 7.72186C15.3107 8.40477 14.1112 8.74254 13.5014 10C13.327 10.3595 13.3345 10.7108 13.4183 11.0163C13.4786 11.2358 13.5171 11.4745 13.5177 11.708C13.5197 12.4629 12.7562 13.0082 12.0014 13C10.0371 12.9786 8.75117 11.3955 8.57613 9.44737Z"
                        fill="#FEB73D"
                      />
                      <path
                        d="M13.4365 18.2761C14.4246 16.414 17.7182 16.414 17.7182 16.414C21.1502 16.3782 21.6138 14.2944 21.9237 13.2412C21.369 17.7226 17.8494 21.2849 13.3885 21.9046C13.0659 21.2256 12.6837 19.6946 13.4365 18.2761Z"
                        fill="#FEB73D"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="100%"
                      height="100%"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        opacity="0.5"
                        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                        fill="white"
                      />
                      <path
                        d="M8.57613 9.44737C8.38887 7.36316 6.78157 5.42105 6.00132 4.71053L5.57031 4.34189C7.30889 2.88037 9.5523 2 12.0014 2C14.2147 2 16.2602 2.7191 17.9168 3.93642C18.1508 4.64695 17.705 6.13158 17.2369 6.84211C17.0673 7.09947 16.6828 7.41898 16.2612 7.72186C15.3107 8.40477 14.1112 8.74254 13.5014 10C13.327 10.3595 13.3345 10.7108 13.4183 11.0163C13.4786 11.2358 13.5171 11.4745 13.5177 11.708C13.5197 12.4629 12.7562 13.0082 12.0014 13C10.0371 12.9786 8.75117 11.3955 8.57613 9.44737Z"
                        fill="white"
                      />
                      <path
                        d="M13.4365 18.2761C14.4246 16.414 17.7182 16.414 17.7182 16.414C21.1502 16.3782 21.6138 14.2944 21.9237 13.2412C21.369 17.7226 17.8494 21.2849 13.3885 21.9046C13.0659 21.2256 12.6837 19.6946 13.4365 18.2761Z"
                        fill="white"
                      />
                    </svg>
                  )}
                </PlanetItem>
                <Typography
                  size="s"
                  weight="medium"
                  color={active ? Colors.yellow400 : Colors.white100}
                  ml="0.4"
                >
                  {c}
                </Typography>
                <ArrowItem>
                  {active ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="100%"
                      height="100%"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M7.5 4.16699L12.5 10.0003L7.5 15.8337"
                        stroke="#FEB73D"
                        stroke-width="1.25"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="100%"
                      height="100%"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M7.5 4.1665L12.5 9.99984L7.5 15.8332"
                        stroke="white"
                        stroke-width="1.25"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  )}
                </ArrowItem>
              </ContinentItem>
            );
          })}
        </ContinentsContainer>
        <CountriesContainer>
          {activeContinent && (
            <CountryItem>
              <Typography size="s" weight="semibold" color={Colors.white100}>
                Select/Deselect All
              </Typography>
              <CheckBox
                defaultState={selectAll}
                onChange={() => {
                  if (selectAll) {
                    removeCountriesByContinent(activeContinent);
                  } else {
                    addCountriesByContinent(activeContinent);
                  }
                }}
              />
            </CountryItem>
          )}

          {Countries.map(({ continent, country }, i) => {
            if (continent !== activeContinent) {
              return null;
            }
            return (
              <CountryItem key={i}>
                <Typography size="s" weight="semibold" color={Colors.white100}>
                  {country}
                </Typography>
                <CheckBox
                  defaultState={isCountryInArray(country)}
                  onChange={() => {
                    if (isCountryInArray(country)) {
                      removeCountry(country);
                    } else {
                      addCountry(country);
                    }
                  }}
                />
              </CountryItem>
            );
          })}
        </CountriesContainer>
      </Body>
    </ModalTemplate>
  );
};

export default EditCountries;

const Body = styled.div`
  display: flex;
  align-items: start;
  max-height: 34rem;
`;

const ContinentsContainer = styled.div`
  width: 50%;
  border-right: 1px solid ${Colors.grey600};
  padding: 1.6rem 0;
  padding-right: 4rem;
`;

const ContinentItem = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 2.4rem;

  &:last-child {
    margin-bottom: 0;
  }
`;
const PlanetItem = styled.div`
  width: 2.4rem;
  height: 2.4rem;
`;

const ArrowItem = styled.div`
  margin-left: auto;
  width: 2rem;
  height: 2rem;
`;

const CountriesContainer = styled.div`
  width: 50%;
  padding: 1.6rem 0;
  padding-left: 4rem;
  overflow: scroll;
  max-height: 34rem;
`;

const CountryItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 1rem;
`;
